import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';

interface Asset {
  id: string;
  name: string;
  type: 'property' | 'account' | 'investment' | 'vehicle' | 'other';
  value: number;
  description: string;
  assignedTo?: string;
  percentage?: number;
  dollarAmount?: number;
}

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  assignedAssets: Asset[];
}

interface DistributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: Asset | null;
  familyMember: FamilyMember | null;
  onConfirm: (assetId: string, familyMemberId: string, percentage: number, dollarAmount?: number) => void;
}

const DistributionModal: React.FC<DistributionModalProps> = ({
  isOpen,
  onClose,
  asset,
  familyMember,
  onConfirm,
}) => {
  const [distributionType, setDistributionType] = useState<'percentage' | 'dollar'>('percentage');
  const [percentage, setPercentage] = useState(100);
  const [dollarAmount, setDollarAmount] = useState(asset?.value || 0);

  useEffect(() => {
    if (asset) {
      setDollarAmount(asset.value);
    }
  }, [asset]);

  const handleConfirm = () => {
    if (asset && familyMember) {
      onConfirm(
        asset.id,
        familyMember.id,
        percentage,
        distributionType === 'dollar' ? dollarAmount : undefined
      );
      onClose();
    }
  };

  if (!isOpen || !asset || !familyMember) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">
            Assign Asset to {familyMember.name}
          </h3>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-4">
          <h4 className="font-medium text-stone-900 mb-2">{asset.name}</h4>
          <p className="text-sm text-stone-600 mb-2">{asset.description}</p>
          <p className="text-sm font-medium text-green-600">Value: ${asset.value.toLocaleString()}</p>
      </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Distribution Type
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDistributionType('percentage')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  distributionType === 'percentage'
                    ? 'bg-green-600 text-white'
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                Percentage
              </button>
              <button
                type="button"
                onClick={() => setDistributionType('dollar')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  distributionType === 'dollar'
                    ? 'bg-green-600 text-white'
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                Dollar Amount
              </button>
            </div>
            </div>
            
          {distributionType === 'percentage' ? (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Percentage: {percentage}%
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={percentage}
                onChange={(e) => setPercentage(Number(e.target.value))}
                className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-stone-500 mt-1">
                <span>1%</span>
                <span>100%</span>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Dollar Amount
              </label>
              <input
                type="number"
                value={dollarAmount}
                onChange={(e) => setDollarAmount(Number(e.target.value))}
                max={asset.value}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter dollar amount"
              />
              <p className="text-xs text-stone-500 mt-1">
                Maximum: ${asset.value.toLocaleString()}
              </p>
            </div>
                )}
              </div>
              
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
          >
            Cancel
                </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Assign Asset
                </button>
              </div>
      </div>
    </div>
  );
};

interface SortableAssetProps {
  asset: Asset;
  onDragStart: () => void;
}

const SortableAsset: React.FC<SortableAssetProps> = ({ asset, onDragStart }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'property':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'account':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseDown={onDragStart}
      className={`bg-white border border-stone-200 rounded-lg p-4 cursor-move hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="text-green-600">
          {getAssetIcon(asset.type)}
              </div>
        <div className="flex-1">
          <h4 className="font-medium text-stone-900">{asset.name}</h4>
          <p className="text-sm text-stone-600">{asset.description}</p>
            </div>
        <div className="text-right">
          <p className="font-semibold text-green-600">${asset.value.toLocaleString()}</p>
          {asset.assignedTo && (
            <p className="text-xs text-stone-500">Assigned</p>
          )}
          </div>
      </div>
    </div>
  );
};

const Frame251: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    asset: Asset | null;
    familyMember: FamilyMember | null;
  }>({
    isOpen: false,
    asset: null,
    familyMember: null,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Load assets from session
    const loadAssets = () => {
      const sessionAssets: Asset[] = [];
      
      // Load properties
      const properties = session.taskProgress?.properties?.answers?.properties || [];
      properties.forEach((prop: unknown) => {
        sessionAssets.push({
          id: `property-${prop.id}`,
          name: prop.address || 'Property',
          type: 'property',
          value: prop.netWorth || 0,
          description: `${prop.type || 'Property'} at ${prop.address || 'Unknown location'}`,
        });
      });

      // Load accounts
      const accounts = session.taskProgress?.personal?.answers?.accounts || [];
      accounts.forEach((account: unknown) => {
        sessionAssets.push({
          id: `account-${account.id}`,
          name: account.institution || 'Account',
          type: 'account',
          value: account.balance || 0,
          description: `${account.type || 'Account'} at ${account.institution || 'Unknown institution'}`,
        });
      });

      // Add some sample assets if none exist
      if (sessionAssets.length === 0) {
        sessionAssets.push(
          {
            id: 'sample-1',
            name: 'Primary Residence',
            type: 'property',
            value: 450000,
            description: 'Family home in suburban area',
          },
          {
            id: 'sample-2',
            name: 'Savings Account',
            type: 'account',
            value: 25000,
            description: 'Emergency fund at local bank',
          },
          {
            id: 'sample-3',
            name: 'Investment Portfolio',
            type: 'investment',
            value: 150000,
            description: 'Diversified stock and bond portfolio',
          }
        );
      }

      setAssets(sessionAssets);
    };

    // Load family members from session
    const loadFamilyMembers = () => {
      const executors = session.taskProgress?.executors?.answers || {};
      const members: FamilyMember[] = [];

      // Add selected executors
      if (executors.selectedExecutors) {
        executors.selectedExecutors.forEach((executor: unknown) => {
          members.push({
            id: `executor-${executor.id}`,
            name: executor.name,
            relationship: executor.relationship,
            assignedAssets: [],
          });
        });
      }

      // Add invited family members
      if (executors.invitedExecutors) {
        executors.invitedExecutors.forEach((member: unknown) => {
          members.push({
            id: `family-${member.id}`,
            name: member.name,
            relationship: member.relationship,
            assignedAssets: [],
          });
        });
      }

      // Add sample family members if none exist
      if (members.length === 0) {
        members.push(
          {
            id: 'sample-1',
            name: 'Sarah Johnson',
            relationship: 'Spouse',
            assignedAssets: [],
          },
          {
            id: 'sample-2',
            name: 'Michael Johnson',
            relationship: 'Son',
            assignedAssets: [],
          },
          {
            id: 'sample-3',
            name: 'Emily Johnson',
            relationship: 'Daughter',
            assignedAssets: [],
          }
        );
      }

      setFamilyMembers(members);
    };

    loadAssets();
    loadFamilyMembers();
  }, [session]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeAsset = assets.find(asset => asset.id === active.id);
      const targetMember = familyMembers.find(member => member.id === over.id);

      if (activeAsset && targetMember) {
        setModalState({
          isOpen: true,
          asset: activeAsset,
          familyMember: targetMember,
        });
      }
    }
  };

  const handleAssetAssignment = (assetId: string, familyMemberId: string, percentage: number, dollarAmount?: number) => {
    // Update asset assignment
    setAssets(prevAssets =>
      prevAssets.map(asset =>
        asset.id === assetId
          ? {
              ...asset,
              assignedTo: familyMemberId,
              percentage: dollarAmount ? undefined : percentage,
              dollarAmount: dollarAmount || undefined,
            }
          : asset
      )
    );

    // Update family member's assigned assets
    setFamilyMembers(prevMembers =>
      prevMembers.map(member =>
        member.id === familyMemberId
          ? {
              ...member,
              assignedAssets: [
                ...member.assignedAssets,
                assets.find(asset => asset.id === assetId)!,
              ],
            }
          : member
      )
    );
  };

  const handleFamilyMemberClick = (member: FamilyMember) => {
    // Show assigned assets for this member
    console.log('Assigned assets for', member.name, ':', member.assignedAssets);
    // You could implement a modal or expand the card to show details
  };

  const handleComplete = () => {
    // Save distribution data to session
    updateTaskProgress('assetDistribution', {
      status: 'completed',
      completedAt: new Date().toISOString(),
      assets: assets,
      familyMembers: familyMembers,
    });

    completeTask('assetDistribution');
    navigate('/frame-140');
  };

  const handleBack = () => {
    navigate('/frame-250');
  };

  const unassignedAssets = assets.filter(asset => !asset.assignedTo);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <BrandSymbol size="lg" className="mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-stone-900 mb-2">
              Asset Distribution
            </h1>
            <p className="text-stone-600">
              Drag assets to family members to assign ownership percentages
            </p>
                </div>
                
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Assets Column */}
              <div>
                <h2 className="text-xl font-semibold text-stone-900 mb-4">
                  Available Assets ({unassignedAssets.length})
                </h2>
                <div className="space-y-3">
                  <SortableContext items={unassignedAssets.map(asset => asset.id)} strategy={verticalListSortingStrategy}>
                    {unassignedAssets.map((asset) => (
                      <SortableAsset
                        key={asset.id}
                        asset={asset}
                        onDragStart={() => {}}
                      />
                    ))}
                  </SortableContext>
                </div>
              </div>
              
              {/* Family Members Column */}
              <div>
                <h2 className="text-xl font-semibold text-stone-900 mb-4">
                  Family Members & Trustees
                </h2>
                <div className="space-y-4">
                  {familyMembers.map((member) => (
                    <div
                      key={member.id}
                      onClick={() => handleFamilyMemberClick(member)}
                      className="bg-white border border-stone-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-medium text-stone-900">{member.name}</h3>
                          <p className="text-sm text-stone-600">{member.relationship}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-green-600">
                            {member.assignedAssets.length} assets
                          </p>
                          <p className="text-xs text-stone-500">
                            Drop assets here
                          </p>
                        </div>
                      </div>

                      {member.assignedAssets.length > 0 && (
                        <div className="border-t border-stone-100 pt-3">
                          <h4 className="text-sm font-medium text-stone-700 mb-2">Assigned Assets:</h4>
                          <div className="space-y-2">
                            {member.assignedAssets.map((asset) => (
                              <div key={asset.id} className="flex items-center justify-between text-sm">
                                <span className="text-stone-600">{asset.name}</span>
                                <span className="font-medium text-green-600">
                                  {asset.percentage ? `${asset.percentage}%` : `$${asset.dollarAmount?.toLocaleString()}`}
                                </span>
                              </div>
                            ))}
              </div>
            </div>
                      )}
          </div>
        ))}
      </div>
    </div>
            </div>
          </DndContext>

          {/* Instructions */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">How to distribute assets</h4>
                <p className="text-sm text-blue-700">
                  Drag assets from the left column to family members on the right. You can assign percentages (1-100%) or specific dollar amounts for assets that will be sold.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Distribution Modal */}
        <DistributionModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState({ isOpen: false, asset: null, familyMember: null })}
          asset={modalState.asset}
          familyMember={modalState.familyMember}
          onConfirm={handleAssetAssignment}
        />
      </ResponsiveContent>

      <ResponsiveFooter
        title="Asset Distribution"
        subtitle="Asset Distribution"
        progressPercent={100}
        onBack={handleBack}
        onNext={handleComplete}
        backText="← Back to Payment"
        nextText="Complete Distribution"
      />
    </ResponsiveContainer>
  );
};

export default Frame251; 